import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';

export default function TestimonialsSlider() {
  const [testimonials, setTestimonials] = useState([]);
  const [expandedMap, setExpandedMap] = useState({});
  const [isPaused, setIsPaused] = useState(false);
  const scrollContainerRef = useRef(null);
  const animRef = useRef(null);
  const scrollPosRef = useRef(0);

  const defaultTestimonials = [
    {
      author: 'Mand',
      age: '52 år',
      quote: '"Marie, du er en dygtig håndværker indenfor dit virkefelt. Jeg har det som en gammel veteranbil som var skrottet og opgivet, men som efter en renovering af en dygtig, nænsom og ærlig håndværker igen har fået nyt liv. Og nu fremstår strømlinet i alt sin pragt og lyst til mange nye kilometer ud i den lysende fremtid".'
    },
    {
      author: 'Kvinde',
      age: '40 år',
      quote: '"Marie formår lynhurtigt at spotte, hvilket menneske man er og tager udgangspunkt i det og dig. Hun er kreativ, fagligt dygtig og et fantastisk glad, inspirerende og varmt menneske, som man ved første øjekast føler sig tryg ved. Hun vil dig kun det bedste."'
    },
    {
      author: 'Mor til datter',
      age: 'på 11 år',
      quote: '"Min datter på 11 år har været i et forløb ved Marie. Marie har en skøn og ligefrem måde at tilgå kommunikationen i børnehøjde, så min datter åbnede op og kunne arbejde med de problematikker hun kom med. Marie finder givende og konkrete veje ind, så der kan arbejdes håndgribeligt og på barnets niveau."'
    },
    {
      author: 'Mand',
      age: '31 år',
      quote: '"At gå i terapi hos dig vil jeg beskrive således: Jeg føler, det er som at have en samtale med mig selv hos en, der ikke har følelser i klemme. Og via dig får jeg stilled de spørgsmål, jeg ikke får stillet mig selv. På den måde får jeg i terapien tid og rum til refleksion. Og derudover, så er du ærlig og det gør mig tryg. Jeg ser altid frem til at snakke med dig og på svære dage tæller jeg ned til, at jeg har min næste tid hos dig”.'
    }
  ];

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/v2/content/testimonials')
      .then((res) => res.json())
      .then((data) => {
        if (data && data.items && data.items.length > 0) {
          setTestimonials(data.items);
        } else {
          setTestimonials(defaultTestimonials);
        }
      })
      .catch(() => {
        setTestimonials(defaultTestimonials);
      });
  }, []);

  const itemsList = testimonials.length > 0 ? testimonials : defaultTestimonials;
  // Duplicate items to ensure a seamless infinite loop
  const duplicatedList = [...itemsList, ...itemsList, ...itemsList, ...itemsList];

  // Butter-smooth continuous gliding loop via requestAnimationFrame
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    let speed = 0.5; // Smooth, clearly moving auto-scroll speed

    const step = () => {
      if (!isPaused && container) {
        scrollPosRef.current += speed;

        const halfWidth = container.scrollWidth / 2;
        if (scrollPosRef.current >= halfWidth) {
          scrollPosRef.current -= halfWidth;
        }

        container.scrollLeft = scrollPosRef.current;
      } else if (container) {
        // Sync ref when paused so manual scrolling is seamless
        scrollPosRef.current = container.scrollLeft;
      }
      animRef.current = requestAnimationFrame(step);
    };

    animRef.current = requestAnimationFrame(step);

    return () => {
      if (animRef.current) {
        cancelAnimationFrame(animRef.current);
      }
    };
  }, [isPaused, duplicatedList.length]);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -360, behavior: 'smooth' });
      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollPosRef.current = scrollContainerRef.current.scrollLeft;
        }
      }, 350);
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 360, behavior: 'smooth' });
      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollPosRef.current = scrollContainerRef.current.scrollLeft;
        }
      }, 350);
    }
  };

  const toggleExpand = (idx) => {
    setExpandedMap((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  const renderQuoteText = (q) => {
    if (!q) return '';
    let str = typeof q === 'string' ? q : '';
    if (typeof q === 'object' && q !== null) {
      if (q.da) str = q.da;
      else if (q.en) str = q.en;
      else str = JSON.stringify(q);
    }
    str = str.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim();
    return str.replace(/^["”'“]+|["”'“]+$/g, '').trim();
  };

  return (
    <section className="py-16 bg-[#5f7365] text-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div className="space-y-1">
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
              Hvad klienter fortæller om terapien
            </h2>
            <p className="text-xs text-[#A1B5A9] italic">
              Alle citater er anonymiserede og videregivet efter tilladelse
            </p>
          </div>
          <Link
            to="/udtalelser"
            className="text-white hover:text-white/80 font-semibold text-xs sm:text-sm transition-all duration-200 hover:-translate-y-1 inline-flex items-center justify-center gap-2 shrink-0 self-start sm:self-auto"
          >
            Se alle udtalelser <i className="fa-regular fa-arrow-right text-xs text-white" aria-hidden="true" />
          </Link>
        </div>

        {/* Scrollable Slider Track */}
        <div 
          className="relative w-full py-2"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          onTouchStart={() => setIsPaused(true)}
          onTouchEnd={() => setIsPaused(false)}
        >
          <div
            ref={scrollContainerRef}
            className="flex gap-6 overflow-x-auto py-2 px-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          >
            {duplicatedList.map((item, idx) => {
              const fullQuote = renderQuoteText(item.quote || item.text);
              const isExpanded = !!expandedMap[idx];
              const isLong = fullQuote.length > 100;
              const displayQuote = isLong && !isExpanded ? `${fullQuote.slice(0, 100)}...` : fullQuote;

              return (
                <div
                  key={idx}
                  className="w-[280px] sm:w-[320px] shrink-0 flex flex-col justify-between select-none p-6 bg-[#4e6053] text-white border border-white/10"
                >
                  <div className="space-y-3">
                    <div className="text-[#C6A868] text-xs flex gap-1.5 items-center" aria-label="5 ud af 5 målskiver">
                      {[...Array(5)].map((_, i) => (
                        <svg
                          key={i}
                          className="w-3.5 h-3.5 fill-current"
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                        >
                          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" />
                          <circle cx="12" cy="12" r="3.5" fill="currentColor" />
                        </svg>
                      ))}
                    </div>
                    <div className="text-sm text-[#E2EAE5] leading-relaxed italic font-sans">
                      "{displayQuote}"
                    </div>
                    {isLong && (
                      <button
                        onClick={() => toggleExpand(idx)}
                        className="text-xs font-semibold text-[#9ECBB0] hover:text-white transition-colors cursor-pointer inline-flex items-center gap-1 pt-1"
                      >
                        {isExpanded ? (
                          <>
                            Vis mindre <i className="fa-regular fa-chevron-up text-[10px]" aria-hidden="true" />
                          </>
                        ) : (
                          <>
                            Læs mere <i className="fa-regular fa-chevron-down text-[10px]" aria-hidden="true" />
                          </>
                        )}
                      </button>
                    )}
                  </div>

                  <div className="pt-4 mt-4 flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-white text-sm">
                        {item.author}
                      </p>
                      <p className="text-xs text-[#9BB3A5]">
                        {item.age}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
